import { useState, useEffect, useCallback, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Activity, Server, Zap, Shield, Cpu, Database, Users, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { doorverwijzing } from "../lib/instellingenadres";
import { useTaal } from "@/contexts/TaalContext";


const CAP_COLORS = {
  llm_generation: "#8b5cf6",
  code_execution: "#ec4899",
  web_browsing: "#14b8a6",
  web_search: "#f59e0b",
  image_generation: "#3b82f6",
  audio_processing: "#10b981",
  data_analysis: "#6366f1",
  storage: "#84cc16",
};

/**
 * G2 (Frank, 18 oktober 2026): het netwerk staat in Instellingen, op het
 * tabblad Netwerk, met een regel met de peers en de verversknop. De oude
 * pagina verwijst sinds G4 door naar dit tabblad.
 */
export function NetwerkBlok() {
  const { t } = useTaal();
  const [status, setStatus] = useState(null);
  const [peers, setPeers] = useState([]);
  const [routing, setRouting] = useState({});
  const [skills, setSkills] = useState([]);
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [nodePopover, setNodePopover] = useState(null);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodePositions = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const [s, p, r, sk, t] = await Promise.all([
        axios.get(`${API}/p2p/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/p2p/peers`).catch(() => ({ data: { peers: [] } })),
        axios.get(`${API}/p2p/routing-table`).catch(() => ({ data: { routing_table: {} } })),
        axios.get(`${API}/p2p/skills`).catch(() => ({ data: { skills: [] } })),
        axios.get(`${API}/p2p/reputation`).catch(() => ({ data: {} })),
      ]);
      setStatus(s.data);
      const peerList = p.data?.peers?.map(p => ({
        ...p,
        latency_ms: p.latency_ms || Math.random() * 50 + 5,
        capabilities: p.capabilities || {},
      })) || [];
      setPeers(peerList);
      setRouting(r.data?.routing_table || {});
      setSkills(sk.data?.skills || []);
      setTrust(t.data);
    } catch (e) {
      // offline mode
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    if (autoRefresh) {
      const iv = setInterval(fetchAll, 5000);
      return () => clearInterval(iv);
    }
  }, [fetchAll, autoRefresh]);

  // ── Canvas force-directed graph ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width = canvas.offsetWidth;
    const H = canvas.height = canvas.offsetHeight;

    const localId = status?.node_id || "local";
    const allNodes = [
      { id: localId, label: "You", isLocal: true, caps: status?.local_capabilities?.categories || [] },
      ...peers,
    ];

    // Assign positions if new nodes appear
    allNodes.forEach(n => {
      if (!nodePositions.current[n.id]) {
        nodePositions.current[n.id] = {
          x: Math.random() * (W - 100) + 50,
          y: Math.random() * (H - 100) + 50,
          vx: 0, vy: 0,
        };
      }
    });

    // Remove stale positions
    Object.keys(nodePositions.current).forEach(id => {
      if (!allNodes.find(n => n.id === id)) delete nodePositions.current[id];
    });

    let running = true;

    function draw() {
      if (!running) return;
      const pos = nodePositions.current;

      // Force-directed simulation (simplified)
      const REPULSION = 20000;
      const ATTRACTION = 0.001;
      const DAMPING = 0.9;
      const CENTER = 0.01;

      allNodes.forEach(a => {
        const pa = pos[a.id];
        if (!pa) return;
        allNodes.forEach(b => {
          if (a.id === b.id) return;
          const pb = pos[b.id];
          if (!pb) return;
          let dx = pa.x - pb.x;
          let dy = pa.y - pb.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = REPULSION / (dist * dist);
          pa.vx += (dx / dist) * force * 0.01;
          pa.vy += (dy / dist) * force * 0.01;
        });
        // Attraction to center
        pa.vx += (W / 2 - pa.x) * CENTER;
        pa.vy += (H / 2 - pa.y) * CENTER;
        // Damping
        pa.vx *= DAMPING;
        pa.vy *= DAMPING;
        pa.x += pa.vx;
        pa.y += pa.vy;
        // Clamp
        pa.x = Math.max(20, Math.min(W - 20, pa.x));
        pa.y = Math.max(20, Math.min(H - 20, pa.y));
      });

      ctx.clearRect(0, 0, W, H);

      // ── Edges ──
      ctx.strokeStyle = "rgba(99, 102, 241, 0.15)";
      ctx.lineWidth = 1;
      allNodes.forEach(a => {
        const pa = pos[a.id];
        if (!pa || a.isLocal) return;
        const local = pos[localId];
        if (local) {
          ctx.beginPath();
          ctx.moveTo(local.x, local.y);
          ctx.lineTo(pa.x, pa.y);
          ctx.stroke();
        }
        // Edges to nearby peers
        allNodes.forEach(b => {
          if (b.id <= a.id || b.isLocal) return;
          const pb = pos[b.id];
          if (!pb) return;
          const dx = pa.x - pb.x;
          const dy = pa.y - pb.y;
          if (Math.sqrt(dx * dx + dy * dy) < 200) {
            ctx.strokeStyle = "rgba(99, 102, 241, 0.08)";
            ctx.beginPath();
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pb.x, pb.y);
            ctx.stroke();
          }
        });
      });

      // ── Nodes ──
      allNodes.forEach(n => {
        const p = pos[n.id];
        if (!p) return;
        const radius = n.isLocal ? 18 : 12;

        // Outer glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 3);
        const hue = n.isLocal ? "var(--accent)" : "var(--primary)";
        grad.addColorStop(0, "rgba(99, 102, 241, 0.3)");
        grad.addColorStop(1, "rgba(99, 102, 241, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 3, 0, Math.PI * 2);
        ctx.fill();

        // Node circle
        ctx.fillStyle = n.isLocal ? "#818cf8" : "#334155";
        ctx.strokeStyle = n.isLocal ? "#a5b4fc" : "#6366f1";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Label
        ctx.fillStyle = "#f1f5f9";
        ctx.font = "10px monospace";
        ctx.textAlign = "center";
        ctx.fillText(n.label?.substring(0, 12) || n.id.substring(0, 8), p.x, p.y + radius + 12);
      });

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => { running = false; if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [peers, status]);

  if (loading) {
    return <p className="py-6 text-center text-muted-foreground">{t("Laden...")}</p>;
  }

  const isOnline = status?.peers_online !== undefined;
  const localCaps = status?.local_capabilities?.categories || [];

  return (
    <div className="space-y-3" data-testid="netwerk-blok">
      <header className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded mr-auto">
          {t("{n} deelnemers online", { n: status?.peers_online || 0 })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => { setLoading(true); fetchAll(); }} data-testid="netwerk-verversen">
          <RefreshCw className={`h-4 w-4 ${autoRefresh ? "text-[hsl(var(--accent))]" : ""}`} />
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* ── Graph visualization ── */}
        <Card className="lg:col-span-3 row-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4" />
                {t("Netwerkkaart")}
              </CardTitle>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)}
                  className="rounded border-muted-foreground" />
                {t("Automatisch verversen")}
              </label>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <canvas ref={canvasRef} className="w-full h-[500px] rounded-b-lg"
              style={{ background: "radial-gradient(ellipse at center, rgba(99,102,241,0.05) 0%, transparent 70%)" }}
              onClick={e => {
                const rect = canvasRef.current.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                for (const [id, pos] of Object.entries(nodePositions.current)) {
                  const dx = mx - pos.x;
                  const dy = my - pos.y;
                  if (Math.sqrt(dx * dx + dy * dy) < 15) {
                    const peer = peers.find(p => p.node_id === id);
                    setNodePopover(peer ? { ...peer, node_id: id } : null);
                    return;
                  }
                }
                setNodePopover(null);
              }}
            />
          </CardContent>
        </Card>

        {/* ── Sidebar stats ── */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Server className="h-4 w-4" /> {t("Jouw node")}</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">ID</span><span className="font-mono">{status?.node_id?.substring(0, 16)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">CPU</span><span>{t("{n} kernen", { n: status?.local_capabilities?.cpu_cores || "-" })}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">RAM</span><span>{status?.local_capabilities?.ram_gb || "-"} GB</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GPU</span><span>{status?.local_capabilities?.gpu || t("Geen")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">VRAM</span><span>{status?.local_capabilities?.vram_gb || 0} GB</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Database className="h-4 w-4" /> {t("Netwerk")}</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Deelnemers")}</span><span>{t("{n} online", { n: status?.peers_online || 0 })}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GPUs</span><span>{status?.network_gpu_count || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">VRAM</span><span>{status?.network_vram_gb || 0} GB</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Opslag")}</span><span>{status?.network_storage_gb || 0} GB</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4" /> {t("Vertrouwen")}</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Gevolgde nodes")}</span><span>{trust?.tracked_nodes || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Betrouwbaar")}</span><span>{trust?.reliable_nodes || 0}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Gem. betrouwbaarheid")}</span><span>{((trust?.network_avg_reliability || 0) * 100).toFixed(0)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Bewijzen")}</span><span>{trust?.total_receipts || 0}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* ── Capability routing table ── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4" /> {t("Routeringstabel")}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(routing).map(([cat, peers]) => (
                <div key={cat} className="text-xs bg-muted/30 rounded p-2 border border-border/40">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CAP_COLORS[cat] || "#6366f1" }} />
                    <span className="font-medium">{cat.replace(/_/g, " ")}</span>
                    <span className="text-muted-foreground ml-auto">{t("{n} deelnemers", { n: peers.length })}</span>
                  </div>
                  {peers.slice(0, 3).map(p => (
                    <div key={p.node_id} className="flex justify-between text-[10px] text-muted-foreground ml-4">
                      <span className="font-mono">{p.node_id?.substring(0, 8)}</span>
                      <span>{(p.score * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── Skills ── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Cpu className="h-4 w-4" /> {t("Agentskills")}</CardTitle></CardHeader>
          <CardContent>
            {skills.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t("Nog geen skills gepubliceerd. Met skills kunnen agenten groeien en nieuwe vermogens delen.")}</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {skills.map(s => (
                  <div key={s.id} className="text-xs bg-muted/30 rounded p-2 border border-border/40 flex items-center justify-between">
                    <div>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-muted-foreground ml-2">v{s.version}</span>
                      <span className="text-[10px] text-muted-foreground ml-2">{t("door {wie}", { wie: s.author_node_id?.substring(0, 8) })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>{s.verification_count} ✓</span>
                      <span>{s.download_count} ↓</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Peer nodes table ── */}
        <Card className="lg:col-span-4">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> {t("Verbonden nodes ({n})", { n: peers.length })}</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-b border-border/40">
                  <tr>
                    <th className="text-left p-2 font-medium">{t("Node-ID")}</th>
                    <th className="text-left p-2 font-medium">{t("Vermogens")}</th>
                    <th className="text-right p-2 font-medium">{t("Vertraging")}</th>
                    <th className="text-right p-2 font-medium">GPU</th>
                    <th className="text-right p-2 font-medium">RAM</th>
                    <th className="text-right p-2 font-medium">VRAM</th>
                    <th className="text-right p-2 font-medium">{t("Opslag")}</th>
                    <th className="text-right p-2 font-medium">{t("Score")}</th>
                  </tr>
                </thead>
                <tbody>
                  {peers.map(p => (
                    <tr key={p.node_id} className="border-b border-border/20 hover:bg-muted/20">
                      <td className="p-2 font-mono">{p.node_id?.substring(0, 12)}</td>
                      <td className="p-2">
                        {(p.capabilities?.categories || []).slice(0, 3).map(c => (
                          <span key={c} className="inline-block text-[9px] px-1.5 py-0.5 rounded mr-1"
                            style={{ backgroundColor: (CAP_COLORS[c] || "#6366f1") + "22", color: CAP_COLORS[c] || "#6366f1" }}>
                            {c.replace(/_/g, " ")}
                          </span>
                        ))}
                      </td>
                      <td className="p-2 text-right">{p.latency_ms?.toFixed(0) || "-"}ms</td>
                      <td className="p-2 text-right">{p.capabilities?.gpu || "—"}</td>
                      <td className="p-2 text-right">{p.capabilities?.ram_gb || "-"} GB</td>
                      <td className="p-2 text-right">{p.capabilities?.vram_gb || 0} GB</td>
                      <td className="p-2 text-right">{p.capabilities?.storage_gb_available?.toFixed(0) || "-"} GB</td>
                      <td className="p-2 text-right">{((p.capabilities?.trust_score || 0) * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                  {peers.length === 0 && (
                    <tr><td colSpan={8} className="text-center p-6 text-muted-foreground">{t("Geen peers verbonden. Start de backend om naar peers te luisteren.")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Node detail popover ── */}
      {nodePopover && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setNodePopover(null)}>
          <Card className="w-80 max-w-[calc(100vw-2rem)]" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-sm font-mono">{nodePopover.node_id?.substring(0, 16)}</CardTitle>
              <CardDescription className="text-xs">{nodePopover.public_url || "Local node"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Vermogens")}</span><span>{(nodePopover.capabilities?.categories || []).join(", ") || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GPU</span><span>{nodePopover.capabilities?.gpu || t("Geen")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">RAM / VRAM</span><span>{nodePopover.capabilities?.ram_gb || "?"} GB / {nodePopover.capabilities?.vram_gb || 0} GB</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Vertrouwensscore")}</span><span>{((nodePopover.capabilities?.trust_score || 0) * 100).toFixed(0)}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Vertraging")}</span><span>{nodePopover.latency_ms?.toFixed(0) || "—"}ms</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("Opslag vrij")}</span><span>{nodePopover.capabilities?.storage_gb_available?.toFixed(1) || "—"} GB</span></div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// G4: de oude route verwijst door naar Instellingen, tabblad Netwerk.
export default function NetworkPage() {
  const location = useLocation();
  return <Navigate to={doorverwijzing("/network", location.search)} replace />;
}
